import React from "react";
import { ScitusLogo } from "./ScitusLogo";

const SANSKRIT = [
    {
        slug: "mativex",
        name: "Mativex",
        logo: "/products/mativex-logo.png",
        logoFit: "mark",
        root: "mati (मति)",
        rootGloss: "intelligence · insight · clarity of a well-formed thought",
        copy: (
            <>
                <strong>Mativex</strong> begins with <em>mati (मति)</em> — the Sanskrit word for
                intelligence, insight, and the clarity of a well-formed thought.
                We paired it with the Latin <em>vex</em> — to animate, to stir into
                motion. Mativex is exactly that: intelligence, animated. Your
                business data, made alive and visible in real time.
            </>
        ),
    },
    {
        slug: "karya",
        name: "Karya",
        logo: "/products/karya-logo.png",
        logoFit: "lockup",
        root: "kārya (कार्य)",
        rootGloss: "work · duty · that which must be done",
        copy: (
            <>
                <strong>Karya</strong> comes from <em>kārya (कार्य)</em> — work, duty, that
                which must be done. Not merely a task, but an obligation. A
                responsibility that belongs to someone. That is what HR is, at its
                best — not administration, but the stewardship of people and their
                contributions.
            </>
        ),
    },
    {
        slug: "vyaahar",
        name: "Vyaahar",
        logo: "/products/vyaahar-logo.png",
        logoFit: "lockup",
        root: "vyavahāra (व्यवहार)",
        rootGloss: "commerce · transaction · conduct of business",
        copy: (
            <>
                <strong>Vyaahar</strong> is drawn from <em>vyavahāra (व्यवहार)</em> — one of
                the richest words in Sanskrit. It means commerce, transaction,
                conduct, and the practice of business. Every invoice, every
                payment, every reconciliation is <em>vyavahāra</em> — the daily conduct
                of trade. We just made it easier.
            </>
        ),
    },
    {
        slug: "kretara",
        name: "Kretara",
        logo: "/products/kretara-logo.svg",
        logoFit: "lockup",
        root: "kretā (क्रेता)",
        rootGloss: "the buyer · the purchaser · the one who commands what comes in",
        copy: (
            <>
                <strong>Kretara</strong> comes from <em>kretā (क्रेता)</em> — the buyer, the
                purchaser, the one who commands what comes in. Procurement is not
                a back-office function. It is a strategic act. Kretara treats it
                that way.
            </>
        ),
    },
];

const LATIN = [
    {
        slug: "attestra",
        name: "Attestra",
        logo: "/products/attestra-logo.png",
        logoFit: "lockup",
        root: "attestari",
        rootGloss: "to bear witness · to certify · to stand behind with your name",
        copy: (
            <>
                <strong>Attestra</strong> comes from <em>attestari</em> — to bear witness, to
                certify, to stand behind something with your name. Every document
                signed through Attestra carries that weight: witnessed,
                timestamped, tamper-evident, and court-admissible.
            </>
        ),
    },
    {
        slug: "acrium",
        name: "Acrium",
        logo: "/products/acrium-logo.svg",
        logoFit: "mark",
        root: "acris",
        rootGloss: "sharp · keen · precise",
        copy: (
            <>
                <strong>Acrium</strong> is rooted in <em>acris</em> — sharp, keen, precise.
                Project cost and procurement control are not about approximation.
                They are about knowing, to the rupee, where you stand against
                budget, commitment, and forecast. Acrium is that sharpness, applied
                to every project, every indent, and every purchase order.
            </>
        ),
    },
    {
        slug: "vexen",
        name: "Vexen",
        logo: "/products/vexen-logo-light.png",
        logoFit: "lockup",
        root: "vex · -en",
        rootGloss: "to stir into motion · to make active",
        copy: (
            <>
                <strong>Vexen</strong> blends Latin <em>vex</em> — to stir, to set in motion —
                with the suffix <em>-en</em>, meaning to make active. Operations left
                unobserved go quiet. Vexen makes them visible, live, and
                actionable.
            </>
        ),
    },
];

const NameCard = ({ item }) => (
    <a
        href={`/products/${item.slug}`}
        className="card etymology-card etymology-card--link"
        data-testid={`etymology-card-${item.slug}`}
        aria-label={`Open ${item.name} product page`}
    >
        <div className="etymology-card-head">
            <span className={`etymology-logo etymology-logo--${item.logoFit}`}>
                <img
                    src={item.logo}
                    alt={`${item.name} logo`}
                    loading="lazy"
                    decoding="async"
                />
            </span>
            <div className="etymology-card-meta">
                <span className="etymology-product-name">{item.name}</span>
                <span className="etymology-root">{item.root}</span>
                <span className="etymology-gloss">{item.rootGloss}</span>
            </div>
        </div>
        <p className="etymology-copy">{item.copy}</p>
        <span className="etymology-card-cta" aria-hidden="true">
            Explore {item.name} →
        </span>
    </a>
);

export const WhatsInAName = () => {
    return (
        <section
            className="section"
            data-testid="about-whats-in-a-name"
            aria-labelledby="about-name-h2"
            style={{
                marginTop: 64,
                paddingTop: 56,
                paddingBottom: 16,
                borderTop: "1px solid var(--border)",
            }}
        >
            <div className="section-head fade-up" style={{ textAlign: "center" }}>
                <span className="eyebrow">Etymology</span>
                <h2
                    id="about-name-h2"
                    className="h2"
                    style={{ marginTop: 16 }}
                    data-testid="whats-in-a-name-h2"
                >
                    What&apos;s in a name?
                </h2>
                <p
                    className="lead"
                    style={{ margin: "10px auto 0", maxWidth: 560 }}
                >
                    Everything, if you ask us.
                </p>
            </div>

            {/* Intro — why Sanskrit and Latin */}
            <div
                className="etymology-intro fade-up"
                data-testid="etymology-intro"
            >
                <p>
                    When we set out to build Scitus, we knew the products needed
                    names that carried meaning — not invented words, not random
                    syllables, not another portmanteau of &ldquo;cloud&rdquo; and
                    &ldquo;sync.&rdquo; We wanted names that were rooted in something
                    real. Something that reflected what the software actually does,
                    and the kind of businesses it was built for.
                </p>
                <p>
                    We went looking in two places: <strong>Sanskrit</strong> and{" "}
                    <strong>Latin</strong>. Not because they are ancient — but because
                    they are precise. These are languages built by people who thought
                    carefully about the world: who had specific words for specific
                    ideas, and meant every syllable.
                </p>
            </div>

            {/* Sanskrit thread */}
            <div className="etymology-thread fade-up" data-testid="etymology-sanskrit">
                <div className="etymology-thread-head">
                    <span className="etymology-thread-badge etymology-thread-badge--sanskrit">
                        Sanskrit
                    </span>
                    <h3 className="etymology-thread-title">
                        Knowledge, commerce, and work.
                    </h3>
                    <p className="muted">
                        Sanskrit is the language of knowledge — and, quietly, the language
                        of Indian commerce. Merchants and operators across centuries used
                        its vocabulary to describe transactions, duties, and the flow of
                        goods and money. When we needed words for what our products do,
                        Sanskrit kept offering the right ones.
                    </p>
                </div>
                <div className="etymology-grid">
                    {SANSKRIT.map((item) => (
                        <NameCard key={item.slug} item={item} />
                    ))}
                </div>
            </div>

            {/* Latin thread */}
            <div className="etymology-thread fade-up" data-testid="etymology-latin">
                <div className="etymology-thread-head">
                    <span className="etymology-thread-badge etymology-thread-badge--latin">
                        Latin
                    </span>
                    <h3 className="etymology-thread-title">
                        Precision, law, and proof.
                    </h3>
                    <p className="muted">
                        Latin is the language of institutions — of contracts, certification,
                        and things that must be recorded and proven. When we needed words for
                        the products that deal with cost, compliance, and commitment, Latin
                        spoke clearly.
                    </p>
                </div>
                <div className="etymology-grid">
                    {LATIN.map((item) => (
                        <NameCard key={item.slug} item={item} />
                    ))}
                </div>
            </div>

            {/* Scitus itself */}
            <div
                className="card etymology-scitus fade-up"
                data-testid="etymology-scitus"
            >
                <div className="etymology-scitus-mark" aria-hidden="true">
                    <ScitusLogo variant="mark" height={56} />
                </div>
                <div>
                    <span className="eyebrow">And Scitus itself?</span>
                    <h3 className="h3" style={{ marginTop: 12 }}>
                        Known. Understood. Sharp.
                    </h3>
                    <p style={{ marginTop: 14 }}>
                        <em>Scitus</em> is Latin for known, understood, intelligent —
                        specifically in the sense of something that has been carefully
                        examined and made clear. It is also, in classical Latin, a word of
                        approval: well done, well made, sharp.
                    </p>
                    <p style={{ marginTop: 14 }}>
                        That is what we are building. Software that makes your business
                        known to you — clearly, completely, in real time. And if we have
                        done our job well, software that earns a quiet <em>scitus</em> from
                        every operator who uses it.
                    </p>
                </div>
            </div>

            {/* Closing stamp */}
            <div
                className="etymology-stamp fade-up"
                data-testid="etymology-stamp"
            >
                <div>
                    <span className="etymology-stamp-num">7</span>
                    <span className="etymology-stamp-label">names</span>
                </div>
                <div>
                    <span className="etymology-stamp-num">2</span>
                    <span className="etymology-stamp-label">languages</span>
                </div>
                <div>
                    <span className="etymology-stamp-num">1</span>
                    <span className="etymology-stamp-label">platform</span>
                </div>
                <p className="etymology-stamp-tag">
                    Built individually. Better together.
                </p>
            </div>
        </section>
    );
};

export default WhatsInAName;
